import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Logo from '../../../../assets/images/common/importerr-no-image-logo.png';
import { TbZoomScan } from 'react-icons/tb';
import { subscribeIcon } from '../../../assets/icons';
import chinaImg from '../../../assets/images/product/china.png';
import indiaImg from '../../../assets/images/product/india.png';
import alibabaImg from '../../../assets/images/product/china.png';
import verifiedSellerBadge from '../../../assets/images/common/verified_seller_badge.png';
import { FaHeart, FaRegHeart } from 'react-icons/fa';
// import alibabaImg from "../../../assets/images/product/alibaba.png";
import { getAuthLocalStorage } from '../../../../storage/localStorage';
import { SubscriptionApiService } from '../../../../service/website/subscriptionApiService';
import { ToastTypes, useMessage } from '../../../../base/context/MessageProvider';
import { useLoader } from '../../../../base/context/LoaderProvider';
import { useSubscriptionProgress } from '../../../../base/context/SubscriptionProgressProvider';
import { normalizeUrl, handleImgError, appendSizeToImageUrl } from '../../../../utils/imageUtils';
import { useSearchParams } from 'react-router-dom';
import { showLoginRestrictionPopup } from '../../common/LoginRestrictionPopup/loginPopup.js';
import { SimilarSearchProgressAPI } from '../SimilarSearchProgress';
import { generateProductUrl } from '../../../../utils/productUrlUtils';
import { fetchToken } from '../../../../lib/globalApiClient';
import { enrichVariantPhysicalsFromProductShipping, getProductShippingInfoForPdp } from '../../views/productDetails/utils';
import './style.css';

// Backend API URL for image proxy
const API_URL = process.env.REACT_APP_APIUrl || 'https://api.importerr.com/api/';

// Cache: one subscription check per offerId (one API call per product, shared by all variants/cards with same offerId)
const subscriptionCacheByOfferId = {};
const subscriptionPromiseByOfferId = {};

function getOrFetchSubscriptionByOfferId(offerId) {
  if (!offerId) return Promise.resolve(null);
  const cached = subscriptionCacheByOfferId[offerId];
  if (cached) return Promise.resolve(cached);
  const inFlight = subscriptionPromiseByOfferId[offerId];
  if (inFlight) return inFlight;
  const promise = import('../../../../network/apiServices/AlibabaSubscriptionApiService')
    .then(({ default: AlibabaSubscriptionApiService }) =>
      AlibabaSubscriptionApiService.checkSubscriptionStatus(offerId)
    )
    .then(res => {
      const data = res?.status?.code === 200 && res?.data
        ? { isSubscribed: !!res.data.isSubscribed, source: res.data.source || null }
        : null;
      subscriptionCacheByOfferId[offerId] = data;
      delete subscriptionPromiseByOfferId[offerId];
      return data;
    })
    .catch(err => {
      console.error('Error checking Alibaba subscription status:', err, { offerId });
      delete subscriptionPromiseByOfferId[offerId];
      return null;
    });
  subscriptionPromiseByOfferId[offerId] = promise;
  return promise;
}

export function clearSubscriptionCacheForOfferId(offerId) {
  if (offerId) {
    delete subscriptionCacheByOfferId[offerId];
    delete subscriptionPromiseByOfferId[offerId];
  }
}

export default function ProductCard({ product, isSelectable = false, isSelected = false, onSelectionChange, keyword = '', index = 0, isShowCalculatingPricing = false, isFromFeaturedCategory = false, onSendInquiry = null }) {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [shippingTime, setShippingTime] = useState(product?.shipping_time_guarantee || product?.shippingTimeGuarantee || '');
  const [shippingCost, setShippingCost] = useState(product?.shipping_cost || '');
  const [imageLoadAttempts, setImageLoadAttempts] = useState(0);
  const [currentImageUrl, setCurrentImageUrl] = useState('');
  const [isNavigating, setIsNavigating] = useState(false);
  const [searchParam] = useSearchParams();
  // 1-Piece availability: show only when isOnePsale is explicitly true
  const isDropshippingAvailable = (() => {
    // 1-piece availability requires MOQ=1 and isOnePsale=true tag
    if (product?.moq !== 1) return false;
    const tagInfoList =
      product?.tagInfoList ||
      product?.detailed_info?.tagInfoList ||
      product?.detailed_info?.productSaleInfo?.tagInfoList ||
      [];
    if (Array.isArray(tagInfoList) && tagInfoList.length > 0) {
      const isOnePsaleTag = tagInfoList.find(tag => tag?.key === 'isOnePsale');
      if (isOnePsaleTag) {
        return isOnePsaleTag.value === true || isOnePsaleTag.value === 'true';
      }
    }

    return product?.isOnePsale === true || product?.isOnePsale === 'true';
  })();

  // Track if product is in DB dynamically (for price color)
  const [isProductInDB, setIsProductInDB] = useState(false);

  // Video state - show video if available (only plays on hover)
  const [videoError, setVideoError] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const videoRef = useRef(null);
  const mainVideoUrl = product?.mainVideo || product?.detailed_info?.mainVideo || null;
  const hasVideo = mainVideoUrl && !videoError;

  // Handle video play/pause on hover
  const handleMouseEnter = () => {
    setIsHovering(true);
    if (videoRef.current && isVideoReady) {
      videoRef.current.play().catch(() => { });
    }
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  // Helper function to find variant image matching search keyword (color matching)
  const findMatchingVariantImage = (product, searchKeyword) => {
    if (!searchKeyword) {
      return null;
    }

    const searchWords = searchKeyword.toLowerCase().trim().split(/\s+/);

    // Common color words to prioritize matching
    const colorKeywords = ['red', 'blue', 'green', 'black', 'white', 'yellow', 'pink', 'purple',
      'orange', 'brown', 'grey', 'gray', 'navy', 'khaki', 'wine', 'coffee',
      'military', 'dark', 'light', 'champagne', 'peacock', 'fluorescent',
      'rose', 'violet', 'beige', 'gold', 'silver'];

    // Find search words that might be colors
    const colorSearchWords = searchWords.filter(word =>
      colorKeywords.some(color => color.includes(word) || word.includes(color))
    );

    // If no color words found, return null
    if (colorSearchWords.length === 0) {
      return null;
    }

    // Use color words for matching
    const wordsToMatch = colorSearchWords;

    // Method 1: Check standard variations format (MongoDB/enriched products)
    const variations = product?.variations || [];
    for (const variation of variations) {
      if (!variation.skuAttributes || !Array.isArray(variation.skuAttributes)) {
        continue;
      }

      for (const attr of variation.skuAttributes) {
        // Only check Color attributes (attributeNameTrans === "Color" or attributeName contains "颜色")
        const isColorAttribute =
          (attr.attributeNameTrans && attr.attributeNameTrans.toLowerCase() === 'color') ||
          (attr.attributeName && attr.attributeName.includes('颜色'));

        if (!isColorAttribute) continue;

        const valueTrans = (attr.valueTrans || '').toLowerCase();
        const value = (attr.value || '').toLowerCase();

        // Check if any search word matches the color value
        for (const word of wordsToMatch) {
          if (valueTrans.includes(word) || value.includes(word) ||
            word.includes(valueTrans) || word.includes(value)) {
            // Found matching color - return the skuImageUrl
            if (attr.skuImageUrl) {
              return attr.skuImageUrl;
            }
          }
        }
      }
    }

    // Method 2: Check Alibaba format (detailed_info.productSkuInfos)
    const skuInfos = product?.detailed_info?.productSkuInfos || [];
    for (const sku of skuInfos) {
      const skuAttributes = sku.skuAttributes || [];

      for (const attr of skuAttributes) {
        // Check if this is a color attribute
        const isColorAttr =
          (attr.attributeNameTrans && attr.attributeNameTrans.toLowerCase() === 'color') ||
          (attr.attributeName && (attr.attributeName.includes('颜色') || attr.attributeName.toLowerCase() === 'color'));

        if (!isColorAttr) continue;

        const valueTrans = (attr.valueTrans || '').toLowerCase();
        const value = (attr.value || '').toLowerCase();

        for (const word of wordsToMatch) {
          if (valueTrans.includes(word) || value.includes(word) ||
            word.includes(valueTrans) || word.includes(value)) {
            // Found matching color - return the skuImageUrl
            if (attr.skuImageUrl) {
              return attr.skuImageUrl;
            }
          }
        }
      }
    }

    return null;
  };

  // Slider state
  const [isSliderOpen, setIsSliderOpen] = useState(false);
  const [sliderUrl, setSliderUrl] = useState('');

  // ⭐ NEW: track when iframe content is ready
  const [isSliderLoaded, setIsSliderLoaded] = useState(false);

  // 🔹 Ref to access iframe DOM
  const sliderIframeRef = useRef(null);

  // 🔹 Hide unnecessary elements ONLY inside slider iframe
  const handleSliderIframeLoad = () => {
    try {
      const iframe = sliderIframeRef.current;
      if (!iframe) return;

      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDoc) return;

      const head = iframeDoc.head || iframeDoc.getElementsByTagName('head')[0];
      if (!head) return;

      const existingStyle = iframeDoc.getElementById('slider-style-overrides');

      if (!existingStyle) {
        const style = iframeDoc.createElement('style');
        style.id = 'slider-style-overrides';
        style.type = 'text/css';

        style.textContent = `
        /* Hide header/footer/etc ONLY inside slider iframe */
        .web_navbar_container,
        .web_footer_bg,
        .after-footer-about {
          display: none !important;
        }

        /* Override layout margin ONLY in slider */
        .web_layout_bg {
          margin-top: 0 !important;
        }

        /* Add padding-bottom ONLY in slider */
        .product_detail_right_section {
          padding-bottom: 70px !important;
        }
      `;

        head.appendChild(style);
      }

      // ✅ At this point, styles are applied → show iframe smoothly
      setIsSliderLoaded(true);
    } catch (error) {
      console.error('Error injecting slider overrides into iframe:', error);
      // Even if something fails, don't block the UI forever
      setIsSliderLoaded(true);
    }
  };

  const message = useMessage();
  const navigate = useNavigate();
  const Location = useLocation();
  const loader = useLoader();
  // Determine if current page is a Global (Alibaba) search result context
  const isGlobalSearchContext = () => {
    try {
      const search = String(window.location.search || '').toLowerCase();
      const path = String(window.location.pathname || '').toLowerCase();
      // Global search contexts observed in app routing
      const hasAliKeyword = search.includes('ali=keyword') || search.includes('ali=upload') || search.includes('ali=url');
      const hasAlibabaCategory = search.includes('category=global');
      const isOnCollection = path.includes('/collection');
      return isOnCollection && (hasAliKeyword || hasAlibabaCategory);
    } catch {
      return false;
    }
  };

  // Require inquiry (hide price, show Send Inquiry) when: no variants, or has variants but all have weight/dimension 0.
  // If any variant (or product) has non-zero weight and dimensions, show price.
  const hasZeroWeightOrDimension = (p) => {
    if (!p) return false;

    const parseNum = (v) => (v === undefined || v === null ? 0 : (typeof v === 'string' ? parseFloat(v) : Number(v)) || 0);
    const getW = (obj) => parseNum(obj?.weight ?? obj?.deadWeight);
    const getH = (obj) => parseNum(obj?.height);
    const getL = (obj) => parseNum(obj?.length);
    const getWd = (obj) => parseNum(obj?.width);
    const isPricable = (obj) => obj && getW(obj) > 0 && getH(obj) > 0 && getL(obj) > 0 && getWd(obj) > 0;

    // ADDITIVE: same as PDP — fill missing weight/dims from productShippingInfo (API) / globalApiResponse (DB)
    const isPricableWithShippingFallback = (productCtx, obj) => {
      if (!productCtx || !obj) return false;
      const e = enrichVariantPhysicalsFromProductShipping(productCtx, getW(obj), getL(obj), getH(obj), getWd(obj));
      const w = parseNum(e.rawWeight);
      const h = parseNum(e.rawHeight);
      const l = parseNum(e.rawLength);
      const wd = parseNum(e.rawWidth);
      return w > 0 && h > 0 && l > 0 && wd > 0;
    };

    const variationsForFallback =
      p.variations || p.variantList || p.detailed_info?.productSkuInfos || [];
    const pdpShipping = getProductShippingInfoForPdp(p);
    const skuShippingDetailsForFallback = pdpShipping?.skuShippingDetails || [];

    // ⚡ Use pre-computed fields from landing page API (avoids sending full variations array)
    if (p.isPricable !== undefined) {
      if (p.isPricable) return false; // has pricable variation → show price
      if (p.hasVariations) {
        // ADDITIVE: backend may mark !isPricable while product-level productShippingInfo still supplies box size/weight
        const fromVariations =
          variationsForFallback.some(v => isPricable(v) || isPricableWithShippingFallback(p, v));
        const fromShipping =
          skuShippingDetailsForFallback.some(d => isPricable(d) || isPricableWithShippingFallback(p, d));
        if (fromVariations || fromShipping) return false;
        return true;
      }
      // no variations → fall through to product-level check below
    }

    const variations = variationsForFallback;
    const skuShippingDetails =
      p.detailed_info?.productShippingInfo?.skuShippingDetails ||
      p.productShippingInfo?.skuShippingDetails ||
      skuShippingDetailsForFallback ||
      [];

    if (variations.length > 0 || skuShippingDetails.length > 0) {
      const fromVariations = variations.some(
        v => isPricable(v) || isPricableWithShippingFallback(p, v)
      );
      const fromShipping = skuShippingDetails.some(
        d => isPricable(d) || isPricableWithShippingFallback(p, d)
      );
      if (fromVariations || fromShipping) return false;
      return true;
    }

    // No variants: show inquiry (Send Inquiry, hide price) unless product itself has full weight/dimensions
    const productShipping = p.detailed_info?.productShippingInfo;
    const mergedShipping = pdpShipping;
    if (
      isPricable(p) ||
      isPricable(productShipping) ||
      isPricable(mergedShipping) ||
      isPricableWithShippingFallback(p, p) ||
      isPricableWithShippingFallback(p, mergedShipping)
    ) {
      return false;
    }
    return true;
  };
  // On /collection route: show inquiry UI when backend flagged product as unable to price
  // (weight/dims = 0 AND no matchedCategory was found during batch pricing)
  // On all other routes we keep this OFF to avoid regressions.
  const isCollectionRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/collection');
  const showInquiryMode = isCollectionRoute && !!product?.requiresInquiry;

  const handleProductNavigation = url => {
    setIsNavigating(true);
    loader.showOverlayLoader('Loading product details...', 'large');

    // Get current search keyword from URL
    const urlParams = new URLSearchParams(window.location.search);
    const searchKeyword = urlParams.get('search');

    // Add search parameter to product URL if it exists
    let finalUrl = url;
    if (searchKeyword) {
      const separator = url.includes('?') ? '&' : '?';
      finalUrl = `${url}${separator}search=${encodeURIComponent(searchKeyword)}`;
    }

    // Small delay to ensure loader shows before navigation
    setTimeout(() => {
      navigate(finalUrl);
    }, 100);
  };

  const handleProductNavigationNewTab = url => {
    // Check login status and PDP limit (per user/IP)
    const auth = getAuthLocalStorage();
    const isLoggedInUser = auth.isLoggedIn && auth.loginHeader;

    if (!isLoggedInUser) {
      // Get current PDP open count from localStorage (tracked per browser/device)
      const pdpOpens = parseInt(localStorage.getItem('userPdpOpenCount') || '0', 10);

      if (pdpOpens >= 4) {
        // Show login popup using component
        showLoginRestrictionPopup('pdp', () => {
          // onLogin callback
          navigate('/login');
        }, () => {
          // onClose callback
        });
        return; // Don't open PDP
      }

      // Increment count before opening (per user/IP)
      localStorage.setItem('userPdpOpenCount', String(pdpOpens + 1));
    }
    setIsNavigating(true);
    loader.showOverlayLoader('Opening product details...', 'large');

    // Get current search keyword from URL
    const urlParams = new URLSearchParams(window.location.search);
    const searchKeyword = urlParams.get('search');

    // Add search parameter to product URL if it exists
    let finalUrl = url;
    if (searchKeyword) {
      const separator = url.includes('?') ? '&' : '?';
      finalUrl = `${url}${separator}search=${encodeURIComponent(searchKeyword)}`;
    }

    // Small delay to ensure loader shows before opening new tab
    setTimeout(() => {
      window.open(finalUrl, '_blank');
      setIsNavigating(false);
      loader.hideLoader();
    }, 100);
  };

  useEffect(() => {
    // console.log('[ProductCard] ========== useEffect STARTED ==========');
    // console.log('[ProductCard] useEffect triggered:', {
    //   isFromFeaturedCategory,
    //   productId: product?._id,
    //   productSku: product?.sku,
    //   hasImgUrl: !!product?.imgUrl,
    //   imgUrlLength: product?.imgUrl?.length,
    //   imgUrlArray: product?.imgUrl,
    //   productKeys: product ? Object.keys(product) : 'no product'
    // });

    // ONLY for featured category products: skip Alibaba API, use MongoDB data
    // Also check product.subscribe directly for real-time updates
    if (isFromFeaturedCategory && product._id) {
      setIsSubscribed(product.subscribe || false);
      // Don't return here - we still need to set the image URL
    }

    // For all other products: original logic
    // Check if this is an Alibaba product
    const isAlibabaProduct =
      String(product?.location || '').toLowerCase() === 'alibaba' || String(product?.location || '').toLowerCase() === 'global' || product?.source === 'alibaba';

    if (isAlibabaProduct) {
      // One subscription check per product (offerId): cache ensures we call API only once per offerId, all cards with same offerId reuse result
      const auth = getAuthLocalStorage();
      const check = auth.isLoggedIn && auth.loginHeader && (auth.userType === 'seller' || auth.userType === 'admin' || auth.userType === 'sub-admin');
      const offerId = product.offerId || product.sku;
      if (check && offerId) {
        getOrFetchSubscriptionByOfferId(offerId).then(data => {
          if (data) {
            setIsSubscribed(data.isSubscribed || false);
            if (data.source === 'products') {
              setIsProductInDB(true);
            }
          } else {
            setIsSubscribed(product.subscribe || false);
          }
        });
      } else {
        setIsSubscribed(product.subscribe || false);
      }
    } else {
      // For regular products, use the subscribe field from database
      // This will update in real-time when product.subscribe changes
      setIsSubscribed(product.subscribe || false);
    }

    // Reset image loading state when product changes
    setImageLoadAttempts(0);

    // Reset video ready state when product changes
    setIsVideoReady(false);
    setVideoError(false);

    // For featured categories, always use imgUrl[0] from MongoDB
    // For Alibaba products, use imgUrl[0] as primary source
    let initialImageUrl;

    // console.log('[ProductCard] About to set image URL:', {
    //   isFromFeaturedCategory,
    //   hasImgUrl: !!product?.imgUrl,
    //   imgUrlLength: product?.imgUrl?.length,
    //   conditionMet: isFromFeaturedCategory && product?.imgUrl?.length > 0
    // });

    // ⭐ Priority 1: Check for white image (product.image) - highest priority as storing the whiteImage in this field for featured categories
    if (product?.image) {
      initialImageUrl = normalizeUrl(product.image) || Logo;
    } else if (isFromFeaturedCategory && product?.imgUrl?.length > 0) {
      // console.log('[ProductCard] ENTERING featured category image URL logic');
      // Featured categories: find first actual image URL (skip route paths)
      // Route paths look like '/fc/new-arrival' or 'http://localhost:3000/fc/new-arrival'
      // console.log('[ProductCard] Featured Category - Checking imgUrl array:', {
      //   productId: product?._id,
      //   productSku: product?.sku,
      //   imgUrlArray: product.imgUrl,
      //   arrayLength: product.imgUrl.length
      // });

      const findValidImageUrl = (urlArray) => {
        for (let i = 0; i < urlArray.length; i++) {
          const url = urlArray[i];
          if (!url || typeof url !== 'string') {
            // console.log(`[ProductCard] URL at index ${i}: Skipped - not a string`, { url, type: typeof url });
            continue;
          }

          const urlLower = url.toLowerCase().trim();
          // Skip if it's a route path (contains /fc/, /product, /collection but no image extension)
          const hasRoutePattern = urlLower.includes('/fc/') || urlLower.includes('/product') || urlLower.includes('/collection');
          const hasImageExtension = /\.(jpg|jpeg|png|gif|webp|svg)(\?|$|#)/i.test(urlLower);
          const isImageCDN = urlLower.includes('alicdn.com') || urlLower.includes('amazonaws.com') || urlLower.includes('cloudfront.net') || urlLower.includes('s3.');

          // console.log(`[ProductCard] URL at index ${i}:`, {
          //   url,
          //   hasRoutePattern,
          //   hasImageExtension,
          //   isImageCDN,
          //   willSkip: hasRoutePattern && !hasImageExtension && !isImageCDN,
          //   willUse: (hasImageExtension || isImageCDN) && !(hasRoutePattern && !hasImageExtension && !isImageCDN)
          // });

          // If it has route pattern but no image extension/CDN, skip it
          if (hasRoutePattern && !hasImageExtension && !isImageCDN) {
            // console.log(`[ProductCard] URL at index ${i}: Skipped - route path detected`);
            continue;
          }

          // If it has image extension or is from CDN, use it
          if (hasImageExtension || isImageCDN) {
            // console.log(`[ProductCard] URL at index ${i}: SELECTED as valid image URL`, { url });
            return url;
          }
        }
        // console.log('[ProductCard] No valid image URL found in array');
        return null;
      };

      const validUrl = findValidImageUrl(product.imgUrl);
      // console.log('[ProductCard] Featured Category - Selected URL:', {
      //   validUrl,
      //   willUseLogo: !validUrl
      // });

      initialImageUrl = validUrl ? normalizeUrl(validUrl) : Logo;

      // console.log('[ProductCard] Featured Category - After normalizeUrl:', {
      //   original: validUrl,
      //   normalized: initialImageUrl,
      //   isLogo: initialImageUrl === Logo
      // });
    } else {
      // Priority for image selection:
      // 1. Backend-provided matchedVariantImage (already processed with search keyword)
      // 2. Frontend matching using variations and search keyword
      // 3. imgUrl[0] as fallback

      // Check if backend already matched a variant image
      if (product?.whiteImage || product?.whiteimage) {
        initialImageUrl = normalizeUrl(product.whiteImage || product.whiteimage) || Logo;
      } else if (product?.isWhiteImageAvailable && product?.image) {
        initialImageUrl = normalizeUrl(product.image) || Logo;
      } else if (product?.matchedVariantImage) {
        initialImageUrl = normalizeUrl(product.matchedVariantImage) || Logo;
      } else {
        // Get search keyword from URL or prop to match variant image
        const urlParams = new URLSearchParams(window.location.search);
        const searchKeyword = keyword || urlParams.get('search') || '';

        // Try to find a variant image that matches the search keyword (e.g., "green" for "green pants")
        const matchingVariantImage = findMatchingVariantImage(product, searchKeyword);

        if (matchingVariantImage) {
          // Use the variant image that matches the search keyword
          initialImageUrl = normalizeUrl(matchingVariantImage) || Logo;
        } else {
          // Fallback: use imgUrl[0]
          initialImageUrl = normalizeUrl(product?.imgUrl?.[0]) || Logo;
        }
      }
    }

    // Always append 200x200 size to image URLs for better performance and consistency
    if (initialImageUrl && initialImageUrl !== Logo) {
      const beforeAppend = initialImageUrl;
      initialImageUrl = appendSizeToImageUrl(initialImageUrl, '400x400');
      // console.log('[ProductCard] After appendSizeToImageUrl:', {
      //   beforeAppend,
      //   afterAppend: initialImageUrl,
      //   changed: beforeAppend !== initialImageUrl,
      //   isFromFeaturedCategory
      // });
    }

    // console.log('[ProductCard] FINAL - Setting currentImageUrl:', {
    //   finalUrl: initialImageUrl,
    //   isLogo: initialImageUrl === Logo,
    //   isFromFeaturedCategory
    // });

    setCurrentImageUrl(initialImageUrl);
  }, [product, product?.subscribe, product?.sku, product?.offerId, isFromFeaturedCategory, keyword]);

  const handleImageError = event => {
    const currentSrc = event.target.src;

    // Log for debugging S3 image issues
    const isS3Url = currentSrc.includes('amazonaws.com') || currentSrc.includes('s3.') || currentSrc.includes('cloudfront.net') || currentSrc.includes('importerr-pimages');
    if (isS3Url) {
      console.log('[ProductCard] S3 image failed to load:', currentSrc);
    }

    // If we're already showing the logo, don't change
    if (currentSrc.includes('logo_no_image.png') || currentSrc.includes('importerr-no-image')) {
      return;
    }

    // Increment attempt counter
    setImageLoadAttempts(prev => prev + 1);

    // Try multiple fallback strategies based on attempt number
    if (imageLoadAttempts === 0) {
      // First attempt: try to fix URL issues (protocol, etc.)
      let fixedSrc = currentSrc;

      // For S3/CDN URLs, try without any modifications first
      if (isS3Url) {
        // For S3/CDN URLs, try the original URL from product
        const originalUrl = product?.image || product?.imgUrl?.[0];
        if (originalUrl && originalUrl !== currentSrc) {
          console.log('[ProductCard] Trying original S3 URL:', originalUrl);
          setCurrentImageUrl(originalUrl);
          event.target.src = originalUrl;
          return;
        }
      }

      // Remove size token if it was added (might be causing issues for S3 URLs)
      // Try original URL without size modification for S3/CDN URLs
      if (fixedSrc.includes('amazonaws.com') || fixedSrc.includes('s3.') || fixedSrc.includes('cloudfront.net')) {
        // For S3/CDN URLs, try the original URL from product without size modification
        if (isFromFeaturedCategory && product?.imgUrl?.[0]) {
          const originalUrl = normalizeUrl(product.imgUrl[0]);
          setCurrentImageUrl(originalUrl);
          event.target.src = originalUrl;
          return;
        }
      }

      if (fixedSrc.includes('http://')) {
        fixedSrc = fixedSrc.replace('http://', 'https://');
      } else if (fixedSrc.startsWith('//')) {
        fixedSrc = 'https:' + fixedSrc;
      }

      if (fixedSrc !== currentSrc) {
        // Append size only if not S3/CDN URL (appendSizeToImageUrl will skip S3 URLs automatically)
        fixedSrc = appendSizeToImageUrl(fixedSrc, '400x400');
        setCurrentImageUrl(fixedSrc);
        event.target.src = fixedSrc;
        return;
      }
    } else if (imageLoadAttempts === 1) {
      // Second attempt: try alternative image URLs from product
      // For featured categories, prioritize imgUrl[0] again, then try other indices
      const alternativeUrls = isFromFeaturedCategory
        ? [
          product?.imgUrl?.[0], // Try index 0 again for featured categories (original URL)
          product?.imgUrl?.[1],
          product?.imgUrl?.[2],
          product?.imageUrl,
          product?.image,
          product?.mainImage,
          product?.productImage,
        ].filter(Boolean)
        : [
          product?.imgUrl?.[0], // Try imgUrl[0]
          product?.imgUrl?.[1],
          product?.imgUrl?.[2],
          product?.imageUrl,
          product?.image,
          product?.mainImage,
          product?.productImage,
        ].filter(Boolean);

      for (const altUrl of alternativeUrls) {
        const normalized = normalizeUrl(altUrl);
        // appendSizeToImageUrl will automatically skip S3/CDN URLs
        const transformed = appendSizeToImageUrl(normalized, '400x400');
        if (transformed && transformed !== currentSrc) {
          setCurrentImageUrl(transformed);
          event.target.src = transformed;
          return;
        }
      }
    }

    // Final fallback: show Upsaleo logo with proper styling
    event.target.src = Logo;
    event.target.style.objectFit = 'fill';
    event.target.style.backgroundColor = '#f5f5f5';
    // event.target.style.padding = '10px';
    event.target.style.borderRadius = '4px';
  };

  // (Existing local normalizeUrl shadowing the import – left untouched as requested)
  const normalizeUrlLocal = url => {
    if (!url) return '';
    try {
      let normalized = String(url).trim();
      if (!normalized) return '';

      // Skip localhost URLs (these are invalid image URLs)
      if (normalized.includes('localhost') || normalized.includes('127.0.0.1')) {
        return '';
      }

      // Handle protocol-relative URLs first
      if (normalized.startsWith('//')) {
        normalized = 'https:' + normalized;
      }

      // Convert http to https
      if (normalized.startsWith('http://')) {
        normalized = normalized.replace('http://', 'https://');
      }

      // If it's not a valid URL, try to make it one
      if (!normalized.startsWith('https://') && !normalized.startsWith('http://')) {
        // If it looks like a domain, add https
        if (normalized.includes('.') && !normalized.includes(' ')) {
          normalized = 'https://' + normalized;
        } else {
          return ''; // Invalid URL
        }
      }

      // Skip URLs that look like app routes (not images)
      if (normalized.includes('/collection') || normalized.includes('/product-detail')) {
        return '';
      }

      return normalized;
    } catch {
      return '';
    }
  };

  // Lazy fetch shipping time guarantee for Global items using product-detail-by-offerid
  useEffect(() => {
    let cancelled = false;

    // ONLY for featured category products: skip Alibaba API calls
    if (isFromFeaturedCategory && product._id) {
      return; // Featured category product - use MongoDB data, no Alibaba API
    }

    // COMPLETE DISABLE: Don't fetch shipping details for ANY Alibaba category searches
    const isInSearchContext =
      window.location.search.includes('search=') ||
      window.location.search.includes('ali=keyword') ||
      window.location.search.includes('ali=upload') ||
      window.location.search.includes('category=global') ||
      window.location.search.includes('category=global');

    // Also disable on any page that might show search results
    const isOnSearchPage = window.location.pathname.includes('/collection') || window.location.pathname.includes('/search') || window.location.pathname.includes('/products');

    if (isInSearchContext || isOnSearchPage) {
      // console.log('Skipping product-detail-by-offerid call - Alibaba search context detected');
      return; // Don't fetch shipping details during any Alibaba search
    }

    const shouldFetch = String(product?.location || '').toLowerCase() === 'alibaba' && (!shippingTime || !shippingCost);
    const offerId = String(product?.sku || '').trim();
    if (!shouldFetch || !offerId) return;

    // Additional check: only fetch if we have shipping time data to fetch
    if (shippingTime && shippingCost) {
      return; // Already have the data, no need to fetch
    }

    // Additional check: ensure this is actually an Alibaba product (not in MongoDB)
    const isAlibabaProduct =
      String(product?.location || '').toLowerCase() === 'alibaba' || String(product?.location || '').toLowerCase() === 'global' || product?.source === 'alibaba';
    if (!isAlibabaProduct) {
      return; // Not an Alibaba product, no need to fetch shipping time
    }

    // AGGRESSIVE DEDUPLICATION: Check if this offerId is already being fetched
    const fetchingKey = `ali_fetching_${offerId}`;
    const fetchingTimestamp = sessionStorage.getItem(fetchingKey);

    // If we have ANY fetching timestamp, skip completely
    if (fetchingTimestamp) {
      const timestamp = parseInt(fetchingTimestamp);
      const now = Date.now();
      // Extended timeout to 30 seconds to prevent rapid retries
      if (now - timestamp < 30000) {
        // 30 seconds
        return; // Already being fetched by another component
      }
    }

    // Check if we have a global lock to prevent multiple simultaneous API calls
    const globalApiLock = sessionStorage.getItem('global_api_lock');
    if (globalApiLock) {
      const lockTimestamp = parseInt(globalApiLock);
      const now = Date.now();
      if (now - lockTimestamp < 5000) {
        // 5 seconds
        return; // Global API lock active
      }
    }

    try {
      const cacheKey = `ali_shiptime_${offerId}`;
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        setShippingTime(cached);
        return;
      }

      // Set global API lock
      sessionStorage.setItem('global_api_lock', Date.now().toString());

      // Mark this offerId as being fetched with timestamp
      sessionStorage.setItem(fetchingKey, Date.now().toString());

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 7000);
      (async () => {
        try {
          const baseUrl = process.env.REACT_APP_APIUrl1688;
          const token = await fetchToken();
          const resp = await fetch(baseUrl + '/product-detail-by-offerid', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-API-Token': token },
            body: JSON.stringify({ offerId, country: 'en' }),
            signal: controller.signal,
          });
          const json = await (async () => {
            try {
              return await resp.json();
            } catch {
              return {};
            }
          })();
          const result = json?.result?.result || json?.result || json || {};
          const stg = result?.productShippingInfo?.shippingTimeGuarantee || result?.shippingInfo?.shippingTimeGuarantee || '';
          // try to pick a shipping/freight price if available
          const freightCandidates = [
            result?.productShippingInfo?.freightPrice,
            result?.shippingInfo?.freightPrice,
            result?.logisticsInfo?.freight,
            result?.freightPrice,
            result?.shippingFee,
            result?.deliveryFee,
            result?.freight,
          ];
          let sc = '';
          for (const f of freightCandidates) {
            if (f == null) continue;
            const s = String(f);
            const m = s.match(/\d+[\d,]*(?:\.\d+)?/);
            if (m) {
              sc = m[0].replace(/,/g, '');
              break;
            }
          }
          if (!cancelled && stg) {
            setShippingTime(stg);
            sessionStorage.setItem(cacheKey, stg);
          }
          if (!cancelled && sc) setShippingCost(sc);
        } catch {
          // ignore
        } finally {
          clearTimeout(timeoutId);
          // Remove fetching flag
          sessionStorage.removeItem(fetchingKey);
          // Remove global API lock
          sessionStorage.removeItem('global_api_lock');
        }
      })();
      return () => {
        cancelled = true;
        clearTimeout(timeoutId);
        controller.abort();
        // Remove fetching flag on cleanup
        sessionStorage.removeItem(fetchingKey);
        // Remove global API lock
        sessionStorage.removeItem('global_api_lock');
      };
    } catch {
      // Remove fetching flag on error
      sessionStorage.removeItem(fetchingKey);
      // Remove global API lock
      sessionStorage.removeItem('global_api_lock');
    }
  }, [product?.sku, product?.location]); // Changed dependencies to be more specific

  // Initialize isProductInDB state based on initial product check
  useEffect(() => {
    const initialCheck = () => {
      // Check explicit source indicators
      if (product._source === 'mongodb' || product._source === 'solr') {
        setIsProductInDB(true);
        return;
      }

      if (product.isFromDB === true) {
        setIsProductInDB(true);
        return;
      }

      // Check if it's explicitly from Ali
      const isFromAli =
        product._source === 'alibaba' ||
        product?.source === 'alibaba' ||
        String(product?.location || '').toLowerCase() === 'alibaba' ||
        String(product?.location || '').toLowerCase() === 'global';

      if (!isFromAli) {
        // If product has MongoDB _id (ObjectId format), it's from DB
        if (product._id && typeof product._id === 'string' && product._id.length === 24 && /^[0-9a-fA-F]{24}$/.test(product._id)) {
          setIsProductInDB(true);
          return;
        }

        // If product has _id but location is not alibaba/global, assume DB
        if (product._id && product.location &&
          String(product.location).toLowerCase() !== 'alibaba' &&
          String(product.location).toLowerCase() !== 'global') {
          setIsProductInDB(true);
          return;
        }
      }

      // Default to false for Alibaba products, will check via API
      setIsProductInDB(false);
    };

    initialCheck();
  }, [product?._id, product?.location, product?._source, product?.isFromDB]);

  // COMMENTED OUT: Was calling checkSubscriptionStatus(offerId) per card on list + polling every 2s - too many variant-level API calls on collection/search.
  // useEffect(() => {
  //   const isAlibabaProduct = ...
  //   const checkIfInDB = async () => {
  //     const res = await AlibabaSubscriptionApiService.checkSubscriptionStatus(identifier);
  //     ...
  //   };
  //   checkIfInDB();
  // }, [product?.offerId, product?.sku, ...]);
  // Periodically check if Alibaba product gets stored in DB - DISABLED for list view (no per-variant API checks)
  useEffect(() => { }, [product?.offerId, product?.sku, product?.location, product?.source, isProductInDB]);

  // ⚡ Listen for background storage completion — auto-update price color when product is stored in DB
  useEffect(() => {
    if (isProductInDB) return; // Already in DB, no need to listen

    const identifier = product?.offerId || product?.sku;
    if (!identifier) return;

    const handleStoredInDB = (event) => {
      const offerIds = event?.detail?.offerIds;
      if (Array.isArray(offerIds) && offerIds.includes(String(identifier))) {
        setIsProductInDB(true);
      }
    };

    window.addEventListener('productsStoredInDB', handleStoredInDB);
    return () => window.removeEventListener('productsStoredInDB', handleStoredInDB);
  }, [product?.offerId, product?.sku, isProductInDB]);

  // Helper function to determine if a product is from DB or Ali based on the product itself
  const isProductFromDB = (product) => {
    // Featured category products are always from DB
    if (isFromFeaturedCategory && product._id) {
      return true;
    }

    // Check explicit source indicators
    if (product._source === 'mongodb' || product._source === 'solr') {
      return true;
    }

    if (product.isFromDB === true) {
      return true;
    }

    // Check if it's explicitly from Ali
    const isFromAli =
      product._source === 'alibaba' ||
      product?.source === 'alibaba' ||
      String(product?.location || '').toLowerCase() === 'alibaba' ||
      String(product?.location || '').toLowerCase() === 'global';

    if (isFromAli) {
      return false;
    }

    // If product has MongoDB _id (ObjectId format), it's from DB
    // MongoDB ObjectIds are 24 character hex strings
    if (product._id && typeof product._id === 'string' && product._id.length === 24 && /^[0-9a-fA-F]{24}$/.test(product._id)) {
      return true;
    }

    // If product has _id but location is not alibaba/global, assume DB
    if (product._id && product.location &&
      String(product.location).toLowerCase() !== 'alibaba' &&
      String(product.location).toLowerCase() !== 'global') {
      return true;
    }

    // Default: if no clear indicators, check if it has offerId (Ali) vs _id (DB)
    // Ali products typically have numeric offerId as sku, DB products have MongoDB _id
    if (product.offerId && !product._id) {
      return false; // Has offerId but no _id, likely Ali
    }

    // If we have _id, assume DB unless explicitly marked as Ali
    return !!product._id;
  };

  // Helper function to get minimum wholesaleFinalPrice from variations
  const getMinWholesalePrice = (product) => {
    // ⚡ Use pre-computed value from landing page API if available
    if (product?.minWholesalePrice != null) return product.minWholesalePrice;

    const variations = product?.variations || [];
    if (variations.length === 0) return null;

    const wholesalePrices = variations
      // .map(v => v.wholesaleFinalPrice)
      .map(v => v.ap)
      .filter(price => price != null && price > 0);

    if (wholesalePrices.length === 0) return null;

    return Math.min(...wholesalePrices);
  };

  // Helper function to find variant with minimum wholesaleFinalPrice
  const getMinPriceVariant = (product) => {
    const variations = product?.variations || [];
    if (variations.length === 0) return null;

    // Filter variations with valid wholesale prices
    const validVariations = variations.filter(v => v.ap != null && v.ap > 0);
    // .filter(v => v.wholesaleFinalPrice != null && v.wholesaleFinalPrice > 0);

    if (validVariations.length === 0) return null;

    // Find variant with minimum wholesaleFinalPrice
    const minVariant = validVariations.reduce((min, current) => {
      // const minPrice = min.wholesaleFinalPrice || Infinity;
      // const currentPrice = current.wholesaleFinalPrice || Infinity;
      const minPrice = min.ap || Infinity;
      const currentPrice = current.ap || Infinity;
      return currentPrice < minPrice ? current : min;
    });

    return minVariant;
  };

  /** Listing cards: count only (combined search may slice `variations` to 1 but send `variationsCount`) */
  const getListedVariantCount = (p) => {
    const fromSkuInfos = p?.detailed_info?.productSkuInfos?.length;
    if (fromSkuInfos) return Number(fromSkuInfos);
    const fromCount = Number(p?.variationsCount);
    if (Number.isFinite(fromCount) && fromCount > 0) return fromCount;
    if (Array.isArray(p?.variations) && p.variations.length > 0) return p.variations.length;
    return 0;
  };

  const check = getAuthLocalStorage().isLoggedIn && getAuthLocalStorage().loginHeader && getAuthLocalStorage().userType === 'seller';
  const isLoggedIn = !!getAuthLocalStorage().isLoggedIn;
  const canSeePrice = isLoggedIn && (getAuthLocalStorage().userType === 'seller' || getAuthLocalStorage().userType === 'admin' || getAuthLocalStorage().userType === 'sub-admin');

  const getProductVariantCount = (productData) => {
    const counts = [
      productData?.detailed_info?.productSkuInfos?.length,
      productData?.variations?.length,
      productData?.variantList?.length,
      productData?.variants?.length,
      productData?.variationsCount,
      productData?.variationCount,
      productData?.variantCount,
      productData?.totalVariants,
      productData?.totalVariations,
    ];

    for (const count of counts) {
      const numericCount = Number(count);
      if (Number.isFinite(numericCount) && numericCount > 0) {
        return numericCount;
      }
    }

    return 0;
  };

  // ⭐ Supplier rating logic - use multiple fallback fields
  const rawRating = Number(
    product?.supplierRating ||
    product?.sellerRating ||
    product?.rating ||
    product?.avgStar ||
    product?.sellerDataInfo?.afterSalesExperienceScore ||
    0
  );
  const rating = isNaN(rawRating) ? 0 : rawRating; // 0–5
  const maxRating = 5;

  const renderSellerStars = () => {
    const activeCount = Math.round(rating);

    return Array.from({ length: maxRating }).map((_, index) => {
      const isActive = index < activeCount;
      return (
        <svg key={index} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={`h-4 w-4 ${isActive ? 'text-yellow-400' : 'text-gray-300'}`}>
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      );
    });
  };

  // ✅ helper to build product URL for View button/slider
  const buildProductUrl = () => {
    // Determine product source individually for each product
    const productIsFromDB = isProductFromDB(product);

    const urlParams = new URLSearchParams(window.location.search);
    const searchKeyword = urlParams.get('search');

    // For products from DB, find variant with minimum wholesale price
    // Otherwise, use first variant's skuId if available
    let variantSkuId = null;
    if (productIsFromDB || isFromFeaturedCategory) {
      const minPriceVariant = getMinPriceVariant(product);
      if (minPriceVariant?.skuId) {
        variantSkuId = minPriceVariant.skuId;
      } else {
        // Fallback to first variant if no minimum price variant found
        variantSkuId = product?.variations?.[0]?.skuId || product?.variants?.[0]?.skuId || null;
      }
    } else {
      // For Alibaba products, use first variant
      variantSkuId = product?.variations?.[0]?.skuId || product?.variants?.[0]?.skuId || null;
    }

    // Use SEO-friendly URL format with offerId for sku/ and variant.skuId for vid/
    if (isFromFeaturedCategory && product._id) {
      return generateProductUrl({
        id: product.offerId || product._id,
        isAli: false,
        title: product.title || product.name || '',
        variantId: variantSkuId,
        search: searchKeyword,
      });
    } else if (productIsFromDB && product._id) {
      // Product from DB - use offerId or _id
      return generateProductUrl({
        id: product.offerId || product._id,
        isAli: false,
        title: product.title || product.name || '',
        variantId: variantSkuId,
        search: searchKeyword,
      });
    } else {
      // Product from Ali - use offerId
      const offerId = product.offerId || product.sku || '';
      return generateProductUrl({
        offer: offerId,
        isAli: true,
        title: product.title || product.name || '',
        variantId: variantSkuId,
        search: searchKeyword,
      });
    }
  };

  // ✅ View button click handler – opens right-side slider
  const handleViewClick = e => {
    e.stopPropagation();
    const finalUrl = buildProductUrl() + '&inSlider=1';
    setIsSliderLoaded(false);
    setSliderUrl(finalUrl);
    setIsSliderOpen(true);
  };

  // Optimized compression settings - shared config
  const COMPRESSION_CONFIG = {
    maxDimension: 400,      // Smaller for faster upload
    targetSizeKB: 80,       // Smaller target
    initialQuality: 0.7,
    minQuality: 0.5,
  };

  // Fast single-pass image compression
  const compressImageFast = async (blob) => {
    const { maxDimension, targetSizeKB, initialQuality, minQuality } = COMPRESSION_CONFIG;

    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);

      img.onload = () => {
        URL.revokeObjectURL(url);

        let { width, height } = img;
        if (width > maxDimension || height > maxDimension) {
          const scale = maxDimension / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        const targetBytes = targetSizeKB * 1024;
        let quality = initialQuality;

        const tryCompress = () => {
          canvas.toBlob((compressedBlob) => {
            if (compressedBlob && (compressedBlob.size <= targetBytes || quality <= minQuality)) {
              resolve(compressedBlob);
            } else {
              quality -= 0.1;
              if (quality >= minQuality) {
                tryCompress();
              } else {
                resolve(compressedBlob || blob);
              }
            }
          }, 'image/jpeg', quality);
        };

        tryCompress();
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(blob);
      };

      img.src = url;
    });
  };

  // Cache for similar search results (module-level for persistence across renders)
  const similarSearchCacheRef = useRef(new Map());
  const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  const findSimilarAbortRef = useRef(null);

  // Extract keywords from title for fallback search
  const extractSearchKeywords = (title) => {
    if (!title) return '';
    const stopWords = new Set([
      'the', 'and', 'for', 'with', 'from', 'that', 'this', 'you', 'your', 'our',
      'are', 'was', 'were', 'will', 'can', 'able', 'into', 'out', 'not', 'non',
      'item', 'items', 'of', 'in', 'on', 'to', 'by', 'is', 'it', 'at', 'as',
      'or', 'an', 'a', 'be', 'has', 'have', 'new', 'sale', 'best', 'high', 'quality',
      'free', 'shipping', 'hot', 'pcs', 'set', 'lot', 'pack', 'wholesale'
    ]);
    const words = title.toLowerCase().replace(/[^\w\s]/g, ' ').split(/\s+/)
      .filter(w => w.length > 2 && !stopWords.has(w));
    words.sort((a, b) => b.length - a.length);
    return words.slice(0, 4).join(' ');
  };

  // Optimized Find Similar Products handler with progress indicator
  const handleFindSimilar = async e => {
    e.stopPropagation();

    // Cancel any pending request
    if (findSimilarAbortRef.current) {
      findSimilarAbortRef.current.abort();
    }
    findSimilarAbortRef.current = new AbortController();
    const { signal } = findSimilarAbortRef.current;

    // Get best image URL with smart fallback
    const isLogoPath = currentImageUrl && (
      currentImageUrl.includes('importerr-no-image') ||
      currentImageUrl.includes('logo_no_image') ||
      !currentImageUrl.startsWith('http')
    );

    const imageCandidates = isLogoPath
      ? [product?.imgUrl?.[0], product?.imgUrl?.[1], product?.imageUrl, product?.image, product?.mainImage]
      : [currentImageUrl, product?.imgUrl?.[0], product?.imageUrl, product?.image, product?.mainImage];

    let imageUrl = null;
    for (const url of imageCandidates) {
      if (typeof url === 'string' && url.trim() && url.startsWith('http')) {
        imageUrl = url.trim();
        break;
      }
    }

    if (!imageUrl) {
      message.showToast('No image available for similar search', ToastTypes.WARNING);
      return;
    }

    // Normalize URL
    let normalizedUrl = imageUrl
      .replace(/^http:\/\//, 'https://')
      .replace(/\.(\d+x\d+)\.(jpg|jpeg|png|webp)/gi, '.$2');

    // Check cache first
    const cacheKey = normalizedUrl.substring(0, 200);
    const cached = similarSearchCacheRef.current.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      console.log('⚡ Using cached results');
      const imageSearchPageSize = parseInt(process.env.REACT_APP_TOTAL_PRODUCTS || '12', 10);
      const timestamp = Date.now().toString();
      const cachedList = (Array.isArray(cached.products) ? cached.products : []).slice(0, imageSearchPageSize);
      sessionStorage.setItem('alibaba_image_results', JSON.stringify(cachedList));
      sessionStorage.setItem('alibaba_image_results_timestamp', timestamp);
      if (cached.pagination) {
        sessionStorage.setItem('alibaba_image_search_pagination', JSON.stringify({ ...cached.pagination, pageSize: imageSearchPageSize }));
      }
      navigate(`/collection?ali=ready&category=global&t=${timestamp}`);
      return;
    }

    // Show progress loader instead of toast
    SimilarSearchProgressAPI.show();
    SimilarSearchProgressAPI.setStage('STARTING');

    try {
      // Stage 1: Fetching image
      SimilarSearchProgressAPI.setStage('FETCHING_IMAGE');

      const proxyUrl = `${API_URL.replace(/\/+$/, '')}/proxy/image?url=${encodeURIComponent(normalizedUrl)}`;
      const imgResponse = await fetch(proxyUrl, { signal });

      if (!imgResponse.ok) {
        throw new Error(`Image fetch failed: ${imgResponse.status}`);
      }

      SimilarSearchProgressAPI.setStage('IMAGE_DOWNLOADED');
      let imgBlob = await imgResponse.blob();

      if (!imgBlob || imgBlob.size === 0) {
        throw new Error('Empty image');
      }

      // Stage 2: Compressing
      SimilarSearchProgressAPI.setStage('COMPRESSING');
      const originalSize = imgBlob.size;
      imgBlob = await compressImageFast(imgBlob);
      console.log(`📦 ${originalSize} → ${imgBlob.size} bytes`);

      SimilarSearchProgressAPI.setStage('COMPRESSED');

      // Convert to base64
      const base64Image = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result?.split(',')[1];
          result ? resolve(result) : reject(new Error('Base64 conversion failed'));
        };
        reader.onerror = reject;
        reader.readAsDataURL(imgBlob);
      });

      // Stage 3: API call
      SimilarSearchProgressAPI.setStage('SENDING_API');

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 35000);

      // Progress to API_PROCESSING after a short delay
      setTimeout(() => SimilarSearchProgressAPI.setStage('API_PROCESSING'), 500);

      const globalApiBase = process.env.REACT_APP_APIUrl1688;
      const imageSearchPageSize = parseInt(process.env.REACT_APP_TOTAL_PRODUCTS || '12', 10);
      const token = await fetchToken();
      const response = await fetch(`${globalApiBase}/get-product-info-by-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Token': token },
        body: JSON.stringify({
          imageBase64: base64Image,
          beginPage: 1,
          pageSize: imageSearchPageSize,
          country: 'en',
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      const productsRaw = data?.products || data?.result?.products || data?.result || data?.data || [];
      const products = (Array.isArray(productsRaw) ? productsRaw : []).slice(0, imageSearchPageSize);

      if (products.length > 0) {
        // Cache results
        similarSearchCacheRef.current.set(cacheKey, {
          products,
          pagination: data.pagination ? { ...data.pagination, pageSize: imageSearchPageSize } : data.pagination,
          timestamp: Date.now(),
        });

        if (similarSearchCacheRef.current.size > 20) {
          const oldestKey = similarSearchCacheRef.current.keys().next().value;
          similarSearchCacheRef.current.delete(oldestKey);
        }

        // Complete the progress
        SimilarSearchProgressAPI.complete();

        if (data.pagination) {
          sessionStorage.setItem('alibaba_image_search_pagination', JSON.stringify({ ...data.pagination, pageSize: imageSearchPageSize }));
        }
        if (data.imageId) {
          sessionStorage.setItem('alibaba_image_search_imageId', data.imageId);
        }
        sessionStorage.setItem('alibaba_image_search_base64', `data:image/jpeg;base64,${base64Image}`);

        const timestamp = Date.now().toString();
        sessionStorage.setItem('alibaba_image_results', JSON.stringify(products));
        sessionStorage.setItem('alibaba_image_results_timestamp', timestamp);

        // Navigate after progress animation completes
        setTimeout(() => {
          navigate(`/collection?ali=ready&category=global&t=${timestamp}`);
        }, 600);
      } else {
        // No results - fallback to keyword search
        SimilarSearchProgressAPI.hide();

        const searchTerms = extractSearchKeywords(product?.title);
        if (searchTerms) {
          message.showToast('Searching by keywords...', ToastTypes.INFO);
          navigate(`/collection?ali=keyword&search=${encodeURIComponent(searchTerms)}&category=global`);
        } else {
          message.showToast('No similar products found', ToastTypes.INFO);
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        SimilarSearchProgressAPI.hide();
        return;
      }

      console.error('❌ Find similar error:', error.message);
      SimilarSearchProgressAPI.error();

      // Smart fallback to keyword search
      const searchTerms = extractSearchKeywords(product?.title);
      if (searchTerms) {
        setTimeout(() => {
          message.showToast('Using keyword search...', ToastTypes.INFO);
          navigate(`/collection?ali=keyword&search=${encodeURIComponent(searchTerms)}&category=global`);
        }, 1200);
      } else {
        setTimeout(() => {
          message.showToast(
            error.message.includes('timeout') ? 'Request timed out' : 'Unable to find similar products',
            ToastTypes.ERROR
          );
        }, 1200);
      }
    }
  };

  const { addSubscribingProduct, updateProductStatus, removeSubscribingProduct } = useSubscriptionProgress();

  const handleSubscribe = () => {
    // console.log('handleSubscribe called', { isSubscribed, check, productSku: product?.sku, productLocation: product?.location, isFromFeaturedCategory });
    if (check) {
      // Featured category products are always MongoDB products, not Alibaba products
      // Check isFromFeaturedCategory first to avoid treating them as Alibaba products
      const isAlibabaProduct = !isFromFeaturedCategory && (
        String(product?.location || '').toLowerCase() === 'alibaba' ||
        String(product?.location || '').toLowerCase() === 'global' ||
        product?.source === 'alibaba'
      );

      // console.log('Subscription check:', { isSubscribed, isAlibabaProduct, isFromFeaturedCategory, productSku: product?.sku });

      if (isSubscribed) {
        // Unsubscribe logic
        if (isAlibabaProduct) {
          // Import the service dynamically
          import('../../../../network/apiServices/AlibabaSubscriptionApiService')
            .then(({ default: AlibabaSubscriptionApiService }) => {
              const toastId = message.showToast('Unsubscribing...', ToastTypes.LOADING);
              AlibabaSubscriptionApiService.unsubscribeFromProduct(product.sku)
                .then(res => {
                  // console.log('Unsubscribe response:', res);
                  if (res && res.status && res.status.code === 200) {
                    const offerId = product.offerId || product.sku;
                    clearSubscriptionCacheForOfferId(offerId);
                    setIsSubscribed(false);
                    message.showToast(res.status.message || 'Successfully unsubscribed', ToastTypes.SUCCESS, { toastId });
                  } else {
                    const errorMsg = res?.error?.message || res?.status?.message || 'Failed to unsubscribe';
                    // console.error('Unsubscribe error response:', res);
                    message.showToast(errorMsg, ToastTypes.ERROR, { toastId });
                  }
                })
                .catch(err => {
                  // console.error('Unsubscribe error:', err);
                  const errorMsg = err?.message || err?.data?.error?.message || err?.data?.message || 'Server error. Try again later';
                  message.showToast(errorMsg, ToastTypes.ERROR, { toastId });
                });
            })
            .catch(importErr => {
              // console.error('Error importing AlibabaSubscriptionApiService:', importErr);
              message.showToast('Failed to load subscription service', ToastTypes.ERROR);
            });
        } else {
          const toastId = message.showToast('Unsubscribing...', ToastTypes.LOADING);
          SubscriptionApiService.unSubscribeProduct(product.sku)
            .then(res => {
              // console.log('Unsubscribe response:', res);
              if (res && res.status && res.status.code === 200) {
                setIsSubscribed(false);
                // Update product object to reflect unsubscribe status
                if (product) {
                  product.subscribe = false;
                }
                message.showToast(res.status.message || 'Successfully unsubscribed', ToastTypes.SUCCESS, { toastId });
              } else {
                const errorMsg = res?.error?.message || res?.status?.message || 'Failed to unsubscribe';
                // console.error('Unsubscribe error response:', res);
                message.showToast(errorMsg, ToastTypes.ERROR, { toastId });
              }
            })
            .catch(err => {
              // console.error('Unsubscribe error:', err);
              const errorMsg = err?.message || err?.data?.error?.message || err?.data?.message || 'Server error. Try again later';
              message.showToast(errorMsg, ToastTypes.ERROR, { toastId });
            });
        }
      } else {
        // Subscribe logic - use background subscription with consolidated toast
        // Get current search keyword from URL
        const urlParams = new URLSearchParams(window.location.search);
        const searchKeyword = urlParams.get('search') || keyword || '';

        // Add product to subscription progress tracker (shows in consolidated toast)
        addSubscribingProduct({
          sku: product.sku,
          title: product.title || product.productName || 'Product',
        });

        // Non-blocking background subscription
        if (isAlibabaProduct) {
          // Import the service dynamically
          import('../../../../network/apiServices/AlibabaSubscriptionApiService').then(({ default: AlibabaSubscriptionApiService }) => {
            // const toastId = message.showToast('Subscribing...', ToastTypes.LOADING);
            AlibabaSubscriptionApiService.subscribeToProduct(product.sku, searchKeyword)
              .then(res => {
                // console.log('Subscribe response:', res);
                if (res && res.status && res.status.code === 200) {
                  clearSubscriptionCacheForOfferId(product.offerId || product.sku);
                  setIsSubscribed(true);
                  // message.showToast(res.status.message || 'Successfully subscribed', ToastTypes.SUCCESS, { toastId });
                  updateProductStatus(product.sku, 'success', 'Subscribed');
                  // Auto-remove after 2 seconds
                  setTimeout(() => removeSubscribingProduct(product.sku), 2000);
                } else {
                  const errorMsg = res?.error?.message || res?.status?.message || 'Failed to subscribe';
                  // console.error('Subscribe error response:', res);
                  // message.showToast(errorMsg, ToastTypes.ERROR, { toastId });
                  updateProductStatus(product.sku, 'error', errorMsg);
                  // Keep error visible for 3 seconds
                  setTimeout(() => removeSubscribingProduct(product.sku), 3000);
                }
              })
              .catch(err => {
                // console.error('Subscribe error:', err);
                const errorMsg = err?.message || err?.response?.data?.message || 'Server error. Try again later';
                updateProductStatus(product.sku, 'error', errorMsg);
                setTimeout(() => removeSubscribingProduct(product.sku), 3000);
              });
          });
        } else {
          // const toastId = message.showToast('Subscribing...', ToastTypes.LOADING);
          SubscriptionApiService.subscribeProduct(product.sku, searchKeyword)
            .then(res => {
              // console.log('Subscribe response:', res);
              if (res && res.status && res.status.code === 200) {
                setIsSubscribed(true);
                // Update product object to reflect subscribe status
                if (product) {
                  product.subscribe = true;
                }
                updateProductStatus(product.sku, 'success', 'Subscribed');
                // Auto-remove after 2 seconds
                setTimeout(() => removeSubscribingProduct(product.sku), 2000);
              } else {
                const errorMsg = res?.error?.message || res?.status?.message || 'Failed to subscribe';
                // console.error('Subscribe error response:', res);
                updateProductStatus(product.sku, 'error', errorMsg);
                setTimeout(() => removeSubscribingProduct(product.sku), 3000);
              }
            })
            .catch(err => {
              // console.error('Subscribe error:', err);
              const errorMsg = err?.message || err?.response?.data?.message || 'Server error. Try again later';
              updateProductStatus(product.sku, 'error', errorMsg);
              setTimeout(() => removeSubscribingProduct(product.sku), 3000);
            });
        }
      }
    } else {
      navigate('/login');
      message.showToast('To subscribe product please login', ToastTypes.WARNING);
    }
  };
  return (
    <>
      <div className={`product_card_box ${isSelected ? 'selected' : ''}`}>
        {/* Verified Seller Badge */}
        {product?.verified_seller && (
          <div className="verified_seller_badge">
            <img src={verifiedSellerBadge} />
          </div>
        )}

        {/* Heart icon for selection/favorite - show if selectable AND dropshipping available OR if subscribed */}
        {(isSelectable && isDropshippingAvailable) || isSubscribed || product?.subscribe ? (
          <div
            onClick={e => {
              e.stopPropagation();
              console.log('Heart icon clicked', { isSubscribed, isSelected, productSku: product?.sku, isFromFeaturedCategory });
              // Always handle subscription - save product to DB and subscribe
              // console.log('Calling handleSubscribe', { isSubscribed, isFromFeaturedCategory });
              handleSubscribe();
              // // For featured category products, always handle subscription
              // // For other products, subscription takes priority over selection
              // if (isFromFeaturedCategory || isSubscribed || product?.subscribe) {
              //   console.log('Calling handleSubscribe', { isSubscribed, isFromFeaturedCategory });
              //   handleSubscribe();
              // } else {
              //   // If not subscribed and not featured category, handle selection
              //   console.log('Calling onSelectionChange');
              //   onSelectionChange && onSelectionChange(product, !isSelected);
              // }
            }}
            className="absolute top-[20px] right-[20px] w-[32px] h-[32px] flex items-center justify-center cursor-pointer z-[2] transition-all duration-200 ease-in-out hover:scale-110 bg-white rounded-[50px]"
            title={isSelected || isSubscribed || product?.subscribe ? 'Unsubscribe' : 'Subscribe'}
          >
            {isSelected || isSubscribed || product?.subscribe ? (
              <FaHeart color="#3DD0E5" size={24} style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} />
            ) : (
              <FaRegHeart color="#000000" size={24} style={{ filter: 'drop-shadow(0 2px 4px rgba(255,255,255,0.8))' }} />
            )}
          </div>
        ) : (
          <></>
        )}

        {(() => {
          // Determine product source individually for each product
          const productIsFromDB = isProductFromDB(product);
          const isAli =
            product?.status != '5' &&
            (String(product.location || '').toLowerCase() === 'alibaba' || String(product.location || '').toLowerCase() === 'global' || product?.source === 'alibaba');

          // Build product URL based on source using SEO-friendly format
          const productUrl = buildProductUrl();

          if (isAli) {
            // For Global products, open in new tab
            return (
              <div
                onClick={() => handleProductNavigationNewTab(productUrl)}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{ cursor: 'pointer', position: 'relative' }}
              >
                {/* 👇 Customer Choice Label conditionally rendered based on showInquiryMode 👇 */}
                {!showInquiryMode && (
                  <div className="customer_choice_label"><span style={{ color: "white", marginRight: "4px" }}>Customer's</span>Choice</div>
                )}
                {/* Always show image first - hide when video is ready AND hovering */}
                <img
                  className="product_card_img"
                  src={currentImageUrl}
                  onError={handleImageError}
                  referrerPolicy="no-referrer"
                  alt={product?.title || 'Product'}
                  onLoad={() => {
                    setImageLoadAttempts(0);
                  }}
                  loading={index < 6 ? 'eager' : 'lazy'}
                  fetchpriority={index < 3 ? 'high' : 'auto'}
                  decoding="async"
                  style={{
                    opacity: hasVideo && isVideoReady && isHovering ? 0 : 1,
                    transition: 'opacity 0.3s ease-in-out'
                  }}
                />
                {/* Video loads in background, shown only on hover when ready */}
                {hasVideo && (
                  <video
                    ref={videoRef}
                    className="product_card_img"
                    src={mainVideoUrl}
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    onCanPlay={() => setIsVideoReady(true)}
                    onError={() => setVideoError(true)}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      opacity: isVideoReady && isHovering ? 1 : 0,
                      transition: 'opacity 0.3s ease-in-out',
                      pointerEvents: isVideoReady && isHovering ? 'auto' : 'none'
                    }}
                  />
                )}
              </div>
            );
          } else {
            // For regular products, use Link component
            return (
              <div
                onClick={() => handleProductNavigationNewTab(productUrl)}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{ cursor: 'pointer', position: 'relative' }}
              >
                {/* 👇 Customer Choice Label conditionally rendered based on showInquiryMode 👇 */}
                {!showInquiryMode && (
                  <div className="customer_choice_label"><span style={{ color: "white", marginRight: "4px" }}>Customer's</span>Choice</div>
                )}

                {/* Always show image first - hide when video is ready AND hovering */}
                <img
                  className="product_card_img"
                  src={currentImageUrl}
                  onError={handleImageError}
                  referrerPolicy="no-referrer"
                  alt={product?.title || 'Product'}
                  onLoad={() => {
                    setImageLoadAttempts(0);
                  }}
                  loading={index < 6 ? 'eager' : 'lazy'}
                  fetchpriority={index < 3 ? 'high' : 'auto'}
                  decoding="async"
                  style={{
                    opacity: hasVideo && isVideoReady && isHovering ? 0 : 1,
                    transition: 'opacity 0.3s ease-in-out'
                  }}
                />
                {/* Video loads in background, shown only on hover when ready */}
                {hasVideo && (
                  <video
                    ref={videoRef}
                    className="product_card_img"
                    src={mainVideoUrl}
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    onCanPlay={() => setIsVideoReady(true)}
                    onError={() => setVideoError(true)}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      opacity: isVideoReady && isHovering ? 1 : 0,
                      transition: 'opacity 0.3s ease-in-out',
                      pointerEvents: isVideoReady && isHovering ? 'auto' : 'none'
                    }}
                  />
                )}
              </div>
            );
          }
        })()}

        {(() => {
          // Determine product source individually for each product
          const productIsFromDB = isProductFromDB(product);
          const isAli =
            product?.status != '5' &&
            (String(product.location || '').toLowerCase() === 'alibaba' || String(product.location || '').toLowerCase() === 'global' || product?.source === 'alibaba');

          // Build product URL based on source using SEO-friendly format
          const productUrl = buildProductUrl();

          if (isAli) {
            // For Global products, open in new tab
            return (
              <div
                onClick={() => {
                  // Check login status and PDP limit
                  const auth = getAuthLocalStorage();
                  const isLoggedInUser = auth.isLoggedIn && auth.loginHeader;

                  if (!isLoggedInUser) {
                    // Get current PDP open count from localStorage (per user/IP)
                    const pdpOpens = parseInt(localStorage.getItem('userPdpOpenCount') || '0', 10);

                    if (pdpOpens >= 4) {
                      // Show login popup using component
                      showLoginRestrictionPopup('pdp', () => {
                        // onLogin callback
                        navigate('/login');
                      }, () => {
                        // onClose callback
                      });
                      return; // Don't open PDP
                    }

                    // Increment count before opening
                    localStorage.setItem('userPdpOpenCount', String(pdpOpens + 1));
                  }

                  // Get current search keyword from URL
                  const urlParams = new URLSearchParams(window.location.search);
                  const searchKeyword = urlParams.get('search');

                  // Add search parameter to product URL if it exists
                  let finalUrl = productUrl;
                  if (searchKeyword) {
                    const separator = productUrl.includes('?') ? '&' : '?';
                    finalUrl = `${productUrl}${separator}search=${encodeURIComponent(searchKeyword)}`;
                  }

                  window.open(finalUrl, '_blank');
                }}
                style={{ cursor: 'pointer' }}
              >
                {Location.pathname !== '/' && getListedVariantCount(product) > 0 && (
                  <p className="product_variant_count">
                    +{getListedVariantCount(product)} variants
                  </p>
                )}
                <p className="product_card_name">{product?.title}</p>
              </div>
            );
          } else {
            // For regular products, use Link component
            return (
              <div
                onClick={() => {
                  // Check login status and PDP limit
                  const auth = getAuthLocalStorage();
                  const isLoggedInUser = auth.isLoggedIn && auth.loginHeader;

                  if (!isLoggedInUser) {
                    // Get current PDP open count from localStorage (per user/IP)
                    const pdpOpens = parseInt(localStorage.getItem('userPdpOpenCount') || '0', 10);

                    if (pdpOpens >= 4) {
                      // Show login popup using component
                      showLoginRestrictionPopup('pdp', () => {
                        // onLogin callback
                        navigate('/login');
                      }, () => {
                        // onClose callback
                      });
                      return; // Don't open PDP
                    }

                    // Increment count before opening
                    localStorage.setItem('userPdpOpenCount', String(pdpOpens + 1));
                  }

                  // Get current search keyword from URL
                  const urlParams = new URLSearchParams(window.location.search);
                  const searchKeyword = urlParams.get('search');

                  // Add search parameter to product URL if it exists
                  let finalUrl = productUrl;
                  if (searchKeyword) {
                    const separator = productUrl.includes('?') ? '&' : '?';
                    finalUrl = `${productUrl}${separator}search=${encodeURIComponent(searchKeyword)}`;
                  }

                  window.open(finalUrl, '_blank');
                }}
                style={{ cursor: 'pointer' }}
              >
                {Location.pathname !== '/' && getListedVariantCount(product) > 0 && (
                  <p className="product_variant_count">
                    +{getListedVariantCount(product)} variants
                  </p>
                )}
                <p className="product_card_name">{product?.title}</p>
              </div>
            );
          }
        })()}

        {(() => {
          // Determine product source individually for each product
          const productIsFromDB = isProductFromDB(product);
          // Use dynamic isProductInDB state or fallback to static check
          const finalIsFromDB = isProductInDB || productIsFromDB;
          const isAli =
            product?.status != '5' &&
            (String(product.location || '').toLowerCase() === 'alibaba' || String(product.location || '').toLowerCase() === 'global' || product?.source === 'alibaba');

          // Build product URL based on source using SEO-friendly format
          const productUrl = buildProductUrl();

          if (isAli) {
            // For Global products, open in new tab
            return (
              <div
                onClick={() => {
                  // Check login status and PDP limit
                  const auth = getAuthLocalStorage();
                  const isLoggedInUser = auth.isLoggedIn && auth.loginHeader;

                  if (!isLoggedInUser) {
                    // Get current PDP open count from localStorage (per user/IP)
                    const pdpOpens = parseInt(localStorage.getItem('userPdpOpenCount') || '0', 10);

                    if (pdpOpens >= 4) {
                      // Show login popup using component
                      showLoginRestrictionPopup('pdp', () => {
                        // onLogin callback
                        navigate('/login');
                      }, () => {
                        // onClose callback
                      });
                      return; // Don't open PDP
                    }

                    // Increment count before opening
                    localStorage.setItem('userPdpOpenCount', String(pdpOpens + 1));
                  }

                  // Get current search keyword from URL
                  const urlParams = new URLSearchParams(window.location.search);
                  const searchKeyword = urlParams.get('search');

                  // Add search parameter to product URL if it exists
                  let finalUrl = productUrl;
                  if (searchKeyword) {
                    const separator = productUrl.includes('?') ? '&' : '?';
                    finalUrl = `${productUrl}${separator}search=${encodeURIComponent(searchKeyword)}`;
                  }

                  window.open(finalUrl, '_blank');
                }}
                style={{ cursor: 'pointer' }}
              >
                {showInquiryMode ? (
                  <div className="product_card_inquiry_text" style={{ color: '#0066cc', fontWeight: 500, fontSize: '14px', marginTop: '4px' }}>
                    Send inquiry to get this product or find similar
                  </div>
                ) : (
                  <>
                    <div className="starting_from_text">Starting from</div>
                    <div className="product_card_price_row">
                      {!isShowCalculatingPricing ? (
                        <p
                          className="product_card_price"
                          style={{ color: finalIsFromDB ? '#0066cc' : '#000000' }}
                        >
                          {(() => {
                            if (Location.pathname === '/') return;

                            // Always use the same price calculation logic regardless of DB status
                            // This ensures price consistency - only color should change, not value
                            // Check if calculate-batch API was called and has breakdown pricing
                            const val = product?.breakdown?.FBP || product?.lowFbp || product?.lowestCalculatedFBP || product?.sp || product?.finalPrice || product?.consign_price;
                            if (!val) return 'Price unavailable';
                            return `₹${val}`;
                          })()}
                        </p>
                      ) : (
                        <p className="product_card_price opacity-70 text-xs">
                          Calculating Price...
                        </p>
                      )}
                      {product?.moq ? (
                        <p
                          className="product_card_moq_simple"
                          style={{ color: '#000000' }}
                        >
                          {(() => {
                            const rawMoq = Number(product.moq) || 0;
                            return `MOQ: ${rawMoq}`;
                          })()}
                        </p>
                      ) : null}
                    </div>
                  </>
                )}
              </div>
            );
          } else {
            // For regular products, use Link component
            return (
              <div
                onClick={() => {
                  // Check login status and PDP limit
                  const auth = getAuthLocalStorage();
                  const isLoggedInUser = auth.isLoggedIn && auth.loginHeader;

                  if (!isLoggedInUser) {
                    // Get current PDP open count from localStorage (per user/IP)
                    const pdpOpens = parseInt(localStorage.getItem('userPdpOpenCount') || '0', 10);

                    if (pdpOpens >= 4) {
                      // Show login popup using component
                      showLoginRestrictionPopup('pdp', () => {
                        // onLogin callback
                        navigate('/login');
                      }, () => {
                        // onClose callback
                      });
                      return; // Don't open PDP
                    }

                    // Increment count before opening
                    localStorage.setItem('userPdpOpenCount', String(pdpOpens + 1));
                  }

                  // Get current search keyword from URL
                  const urlParams = new URLSearchParams(window.location.search);
                  const searchKeyword = urlParams.get('search');

                  // Add search parameter to product URL if it exists
                  let finalUrl = productUrl;
                  if (searchKeyword) {
                    const separator = productUrl.includes('?') ? '&' : '?';
                    finalUrl = `${productUrl}${separator}search=${encodeURIComponent(searchKeyword)}`;
                  }

                  window.open(finalUrl, '_blank');
                }}
                style={{ cursor: 'pointer' }}
              >
                {showInquiryMode ? (
                  <div className="product_card_inquiry_text" style={{ color: '#0066cc', fontWeight: 500, fontSize: '14px', marginTop: '4px' }}>
                    Send inquiry to get this product or find similar
                  </div>
                ) : (
                  <>
                    <div className="starting_from_text">Starting from</div>
                    <div className="product_card_price_row">
                      {!isShowCalculatingPricing ? (
                        <p
                          className="product_card_price opacity-70 text-xs"
                          style={{ color: finalIsFromDB ? '#0066cc' : '#000000' }}
                        >
                          {(() => {
                            // Always use the same price calculation logic regardless of DB status
                            // This ensures price consistency - only color should change, not value
                            // Check if calculate-batch API was called and has breakdown pricing
                            let basePrice = product?.breakdown?.FBP || product?.lowFbp || product?.sp || product?.finalPrice || product?.consign_price;
                            if (!basePrice) return 'Price unavailable';
                            const raw = Number(basePrice).toFixed(2);
                            return `₹${raw}`;
                          })()}
                        </p>
                      ) : (
                        <p className="product_card_price" style={{ opacity: 0.7 }}>
                          Calculating Price...
                        </p>
                      )}
                      {product?.moq ? (
                        <p
                          className="product_card_moq_simple"
                          style={{ color: '#000000' }}
                        >
                          MOQ: {product.moq}
                        </p>
                      ) : null}
                    </div>
                  </>
                )}
              </div>
            );
          }
        })()}

        {(product?.month_sold !== undefined || product?.monthSoldOut !== undefined) && (
          <p className="product_card_country">
            {product?.month_sold || product?.monthSoldOut} sold this month{' '}
            {(() => {
              const loc = String(product.location || '').toLowerCase();
              if (loc === 'china') return <img src={chinaImg} alt="China" />;
              if (loc === 'india') return <img src={indiaImg} alt="India" />;
              if (loc === 'alibaba' || loc === 'global') return <img src={alibabaImg} alt="Alibaba" />;
              return <span>{product.location || 'Unknown'}</span>;
            })()}
          </p>
        )}

        {/* Supplier rating row + 1-piece availability pill */}
        {(isSelectable && isDropshippingAvailable) || (Location.pathname !== '/' && rating > 0) ? (
          <div className="product_card_meta_row">
            {/* Left: 1-piece availability text */}
            {isSelectable && isDropshippingAvailable && (
              <span className="dropshipping_text">1-Piece available</span>
            )}

            {/* Right: Supplier rating */}
            {Location.pathname !== '/' && rating > 0 && (
              <div className="supplier_rating">
                <div className="flex items-center">{renderSellerStars()}</div>
                <span className="text-xs font-semibold text-gray-900">{rating ? rating.toFixed(1) : '—'}</span>
              </div>
            )}
          </div>
        ) : null}

        {/* ✅ View + Find Similar actions row */}
        <div className="product_card_actions">
          <button
            className={`view_button ${showInquiryMode ? 'inquiry_mode' : ''}`}
            onClick={(e) => {
              if (showInquiryMode && onSendInquiry) {
                e.stopPropagation();
                onSendInquiry(product);
              } else {
                handleViewClick(e);
              }
            }}
            title={showInquiryMode ? 'Send inquiry for this product' : 'View product in slider'}
          >
            {showInquiryMode ? 'Send Inquiry' : 'Add to cart'}
          </button>

          <button
            className="find_similar_button"
            onClick={handleFindSimilar}
            title="Find similar products based on image"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
          >
            <TbZoomScan size={18} />
            Find Similar
          </button>
        </div>

        {/* ✅ Category information row */}
        {(product?.topCategoryName || product?.secondCategoryName || product?.thirdCategoryName) && (
          <div className="product_card_categories">
            {product?.topCategoryName && (
              <div className="category_item" title={`Top Category: ${product.topCategoryName}`}>
                <span className="category_label">Top:</span>
                <span className="category_name">{product.topCategoryName}</span>
                {product?.topCategoryId && (
                  <span className="category_id">({product.topCategoryId})</span>
                )}
              </div>
            )}
            {product?.secondCategoryName && (
              <div className="category_item" title={`Second Category: ${product.secondCategoryName}`}>
                <span className="category_label">2nd:</span>
                <span className="category_name">{product.secondCategoryName}</span>
                {product?.secondCategoryId && (
                  <span className="category_id">({product.secondCategoryId})</span>
                )}
              </div>
            )}
            {product?.thirdCategoryName && (
              <div className="category_item" title={`Third Category: ${product.thirdCategoryName}`}>
                <span className="category_label">3rd:</span>
                <span className="category_name">{product.thirdCategoryName}</span>
                {product?.thirdCategoryId && (
                  <span className="category_id">({product.thirdCategoryId})</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ✅ Right-side slider overlay for View button */}
      {isSliderOpen && (
        <div className="product_slider_overlay" onClick={() => setIsSliderOpen(false)}>
          <div className="product_slider_panel" onClick={e => e.stopPropagation()}>
            <button type="button" className="product_slider_close" onClick={() => setIsSliderOpen(false)} aria-label="Close product slider">
              ✕
            </button>

            {sliderUrl && (
              <iframe
                ref={sliderIframeRef}
                src={sliderUrl}
                title={product?.title || 'Product details'}
                className={`product_slider_iframe ${isSliderLoaded ? 'slider_iframe_loaded' : ''}`}
                onLoad={handleSliderIframeLoad}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}
